/**
 * 微博发布文案数据表（剧宣批重排：日常并入营业、工作=剧宣限定、公益保留）
 * 供 WeiboPanel 和 FanSystem 共享
 */
export interface WeiboPostTypeDef {
    /** 类型（营业 / 工作 / 公益），同时直接作为界面展示标签 */
    type: string;
    /** 文案池（随机选一条发布） */
    copyPool: string[];
}

/** 发布类型顺序（tab 栏顺序，默认选中第一个；2026-10-02 用户令「工作」挪末位——营业/公益是日常高频，剧宣靠有可宣剧时的智能落点直达） */
export const POST_TYPES = ['营业', '公益', '工作'] as const;

/** 各类型文案池（⛔ 文案开头带【分类】前缀——分类已由记录行 post-tag 承担，2026-09-28 用户口令全量去除） */
export const WEIBO_POST_TYPES: WeiboPostTypeDef[] = [
    {
        type: '营业',
        copyPool: [
            '感谢大家一路陪伴，未来也请多多关照！',
            '新的一月也要元气满满，请多关照～',
            '你们的支持是我最大的动力！',
            '今天行程满满，但心里很充实！',
            '难得休息，在家撸猫追剧，幸福！',
        ],
    },
    {
        // 工作=剧宣专属：文案走 DRAMA_PROMO_* 模板池填空，本池仅作 tab 存在性占位
        type: '工作',
        copyPool: [],
    },
    {
        type: '公益',
        copyPool: [
            '参加爱心义卖，为山区儿童献一份力。',
            '今天探望了敬老院的爷爷奶奶们，愿平安喜乐。',
            '呼吁大家关注流浪动物，用领养代替购买。',
        ],
    },
];

/** 按类型索引 */
export const WEIBO_MAP: Record<string, WeiboPostTypeDef> = {};
WEIBO_POST_TYPES.forEach(t => { WEIBO_MAP[t.type] = t; });

// ===== 剧宣模板池（审定稿=docs/微博剧宣-文案模板池.md，12+3；占位符 {script}/{role}）=====
// 抽池规则（FanSystem.rollPromoCopy）：ScriptDef.promoCopy 覆盖位优先；无名角色抽 bitPool、有名角色抽 mainPool；
// 开播月（剧宣窗口第一月）改抽 PREMIERE_* 两池（2026-10-24 用户令：开播月剧宣单独开池；
// 本池=AI 初稿待用户删改，改文案须与本档审定稿一起改）
// （mainPool=审定稿通用6+主演向3+参演向3 共 12 条，措辞已对全番位安全：⛔ 龙套专用句不在其中）

/** 主池：一切有名角色（1~5 番/无番位）可抽 */
export const DRAMA_PROMO_MAIN_POOL: string[] = [
    '《{script}》终于和大家见面了！我是{role}，从第一页剧本到上映，这一程值了。',
    '《{script}》剧组所有人的心血都在片子里了，我是{role}，把心跳交给你们。',
    '《{script}》开播！关于{role}的一切，欢迎讨论、锐评、二创，我都爱看。',
    '《{script}》里的那个{role}，从今天起就交给你们了。如果有瞬间让你停留，记得告诉我。',
    '从试镜到播出，《{script}》我走了很久。{role}是我交出的答卷，愿对得起每一秒档期。',
    '《{script}》上了，我在里面等你。不用带礼物，带着弹幕来就行。',
    '《{script}》开播了。作为{billing}，我见证过它每一个深夜的片场，现在，把白天交给你们。',
    '《{script}》是我{billing}的作品，{role}值得我一分钟一分钟讲给你们听。看完来评论区，我都在。',
    '{role}的人生，从今天起交给观众了。《{script}》，我们戏里见。',
    '很幸运以{role}的身份{billing}《{script}》，主创们把梦造好了，我们负责把它讲圆。今晚见！',
    '不吹不黑，《{script}》是我拍的时候就知道会好看的那一类。我在剧中饰演{role}，欢迎围观。',
    '有幸在《{script}》里出演{role}，戏份不多，但每场都当大场面拍的。看了的朋友，欢迎在弹幕里捞我一下～',
];

/** 龙套兜底池：无名角色（=龙套/特约，有名角色不可能无名）专抽 */
export const DRAMA_PROMO_BIT_POOL: string[] = [
    '《{script}》上了！我在里面演了一个没有名字但很认真的角色，眼尖的观众请弹幕捞我～',
    '《{script}》开播，我的名字没进字幕，但那天清早六点的通告我是真起了。大家去看！',
    '《{script}》里有个从我脸前走过的人，那就是我（逃）。反正播出大家都看到了，我就当红过了！',
];

/**
 * 开播月池（2026-10-24 用户令新开：开播月的剧宣走这一池，与播出期常规池分开）
 * 与常规池同口径：{script}=剧名、{role}=角色名、措辞不含类型词与番位数字，四类型通用。
 * 本池为 AI 初稿，待用户删改（审定稿同 docs/微博剧宣-文案模板池.md）。
 */
export const DRAMA_PROMO_PREMIERE_POOL: string[] = [
    '《{script}》就快和大家见面了。倒计时已经开始，我是{role}，见面的那天，我会在。',
    '还没到见面的日子，我已经等了很久。《{script}》里的{role}，敬请期待。',
    '先提前打个招呼。《{script}》很快就来，我是{role}，到时候别错过。',
    '在《{script}》见到{role}之前，我想先说一句：这个角色，我准备得很认真。',
    '《{script}》见面前，先占个位置。{role}的故事，等你们到时候自己看。',
    '作为{billing}，我先替《{script}》说一句：值得等。很快见。',
];

/** 开播月龙套池：无名角色在开播月专抽（不落 {role} 字段） */
export const DRAMA_PROMO_PREMIERE_BIT_POOL: string[] = [
    '《{script}》就快和大家见面了。我在里面演一个没有名字的角色，先替它说一句：敬请期待。',
    '倒计时了。《{script}》见面前，先留个记号：字幕里也许找不到我，镜头里一定有。',
    '《{script}》很快就来。我没有名字，但也跟着一起等这一天。',
];

/** {billing} 映射（用户令：措辞⛔ 出现番位数字）：下标=billing 1~5；无番位（⛔ 不可当主演）取 [5] */
export const BILLING_DISPLAY_LABELS = ['出演', '主演', '主演', '参演', '参演', '客串出演'];
